import {Link} from "react-router";

import {SelectOption} from "@/components/select/Select.types.ts";
import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {EllipsisText} from "@/components/typography/EllipsisText.tsx";
import {BrandInfo} from "@/lib/queries/accounts.ts";
import {StatusPageList} from "@/lib/types/statuspages.ts";
import {StatusPageActions} from "@/pages/statuspages/list/actions/StatusPageActions.tsx";
import {StatusPagePublicUrlIcon} from "@/pages/statuspages/list/components/StatusPagePublicUrlIcon.tsx";
import {StatusPagesEmptyList} from "@/pages/statuspages/list/components/StatusPagesEmptyList.tsx";
import {StatusPageStatusCell} from "@/pages/statuspages/list/components/StatusPageStatusCell.tsx";
import {StatusPageVisibilityCell} from "@/pages/statuspages/list/components/StatusPageVisibilityCell.tsx";

import styles from "./StatusPagesTable.module.scss";

type StatusPagesTable = {
  brand: BrandInfo;
  hasWriteAccess?: boolean;
  hasAdminAccess?: boolean;
  items: StatusPageList[];
  isFilteringApplied: boolean;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  loading: boolean;
  pagination?: LimitAndOffset;
  totalRows?: number;
  updatePagination?: (laf: LimitAndOffset) => void;
  paginationOptions?: SelectOption<number>[];
};

export const StatusPagesTable = ({
  items,
  brand,
  sortColumn,
  sortDirection,
  onSortingChange,
  isFilteringApplied,
  hasWriteAccess = false,
  hasAdminAccess = false,
  loading,
  pagination,
  totalRows,
  updatePagination,
  paginationOptions,
}: StatusPagesTable) => {
  const specs: ColumnSpec<StatusPageList>[] = [
    {
      fieldName: "name",
      fieldTitle: "Name",
      accessor: (obj) => (
        <div className="d-flex align-items-center gap-1">
          <EllipsisText className="d-block text-nowrap pulse-color-blue-500" toolTip={obj.name}>
            <Link
              to={`/statuspages/${obj.id}`}
              className="pulseTextBodySmallMedium pulse-color-blue-500 text-decoration-none"
            >
              {obj.name}
            </Link>
          </EllipsisText>
          {obj.public_url && (
            <StatusPagePublicUrlIcon
              statusPageId={obj.id}
              publicUrl={obj.public_url}
              cnameUrl={obj.cname_url}
              brand={brand}
            />
          )}
        </div>
      ),
      meta: {
        headerClassName: styles.nameColumn,
      },
      enableSorting: true,
    },
    {
      fieldName: "status",
      fieldTitle: "Status",
      accessor: (obj) => <StatusPageStatusCell statusPage={obj} />,
      meta: {
        headerClassName: styles.statusColumn,
      },
    },
    {
      fieldName: "visibility_level",
      fieldTitle: "Visibility",
      accessor: (obj) => <StatusPageVisibilityCell visibilityLevel={obj.visibility_level} />,
      meta: {
        headerClassName: styles.typeColumn,
      },
    },
    {
      fieldName: "subscribers_count",
      fieldTitle: "Subscribers",
      meta: {
        headerClassName: styles.subscribersColumn,
      },
    },
    {
      fieldName: "quick_actions",
      accessor: (obj) => (
        <StatusPageActions
          id={obj.id}
          name={obj.name}
          theme={obj.theme}
          isServedBySpa={!!obj.is_served_by_spa}
          hasWriteAccess={hasWriteAccess}
          hasAdminAccess={hasAdminAccess}
        />
      ),
      meta: {
        headerClassName: styles.actionColumn,
      },
    },
  ];
  return (
    <Table
      fixedWidth
      minWidth={"800px"}
      rows={items}
      sorting={{column: sortColumn, direction: sortDirection}}
      onSortingChange={onSortingChange}
      specs={specs}
      initialState={{columnPinning: {right: ["quick_actions"]}}}
      isLoading={loading}
      noDataComponent={
        <StatusPagesEmptyList variant={isFilteringApplied ? "filter" : "empty"} colSpan={specs.length} />
      }
      pagination={pagination}
      totalRows={totalRows}
      updatePagination={updatePagination}
      paginationOptions={paginationOptions}
    />
  );
};
